/* eslint-disable no-console */
import Store from 'lightningjs-redux';
import { LAYOUT_ENTITIES, LAYOUT_FRONTSPACES } from '@bedrock/fido-sdk';
import { getCurrentLayout, getRequestedEntityType } from '../../logic/layout/layout.selectors';
import { logout } from '../../logic/pairing/pairing.actions';
import { clearCache, loadNavigation, setCurrentSectionCode } from '../../logic/layout/layout.actions';
import { addBookmark, removeBookmark } from '../../logic/bookmark/bookmark.actions';
import {
  navigateToAliasHome,
  navigateToFrontspace,
  navigateToFolderPage,
  navigateToFoldersPage,
  navigateToProfilePage,
  navigateToProgramPage,
  navigateToSearchPage,
  navigateToServicePage,
  navigateToServicesPage,
  navigateToVodPage,
  navigateToLivePage,
  navigateToBookmarksPage,
  navigateToSettingsPage,
  navigateToInformationPage,
  navigateToLivesPage,
  navigateToUsageTermsPage,
  navigateToLegalConditionsPage,
  navigateToAccessibilityStatementPage,
  navigateToLandingPage,
  navigateToPrivacyPolicyPage,
  navigateToAudioPage,
  navigateToFAQPage,
  navigateToFeedbackPage,
  navigateToAccountSettingsPage,
  navigateToRadioPage,
  navigateToSeasonPage,
} from '../routes/routes.utils';
import { getOpenCatalogFeature } from '../../logic/features/features.selectors';
import { sendEvent, overrideGAPageName } from '../../logic/analytics/analytics.utils';
import { handleLock, clearAfterProfileChanged, disableProfileAutoSelect } from '../../logic/lock/lock.actions';
import { setPlayerInitialized } from '../../logic/player/player.constants';
import { resetJwtProfile } from '../../logic/token/token.actions';
import { getVideoSource } from '../../logic/player/player.selectors';
import { ERROR_LEVELS, ERROR_TYPES } from '../error/error.constants';
import { errorAction } from '../../logic/error/error.actions';
import { rememberSSIDeclined } from '../pairing/pairing.utils';
import {
  getItemActionType,
  getItemAnalyticsEvent,
  getSectionCode,
  getTargetData,
  shouldRefreshAliasHome,
} from './cta.utils';
import CTA from './cta.constants';

export const PRESS_BOOKMARKS_BUTTON = 'bookmarks/PRESS_BOOKMARKS_BUTTON';
export const PRESS_LOGOUT_BUTTON = 'logout/PRESS_LOGOUT_BUTTON';

/**
 * Creates item-dependent action that dispatches appropriate Redux actions
 *
 * @param {Object} options all the options
 * @param {Object} options.item item metadata, as retrieved from layout
 * @param {Object} options.analytics item analytics override(if required)
 * @param {string} options.layout layout object, required for Lock actions
 * @param {Function} options.callback will be fired for some of the actions after actions are dispatched
 * @param {string} options.entityId layout entity ID, required for Bookmarks actions
 * @param {string} options.entityType layout entity type, required for Bookmarks actions
 */

export const createItemAction = options => {
  const type = getItemActionType(options.item);
  const targetData = getTargetData(options.item);
  const section = getSectionCode(options.item);
  const event = options.analytics || getItemAnalyticsEvent(options.item);
  const { id } = targetData;
  const reload = shouldRefreshAliasHome(section, Store.getState());

  console.log(`[CTA] Creating action for type: ${type}, id: ${id}, section: ${section}`);

  switch (type) {
    case CTA.AUDIO: {
      return () => {
        sendEvent(event);
        navigateToAudioPage(id);
      };
    }
    case CTA.RADIO: {
      return () => {
        sendEvent(event);
        navigateToRadioPage(id);
      };
    }
    case CTA.BOOKMARKS:
      // eslint-disable-next-line no-unused-vars
      return dispatch => {
        dispatch(setCurrentSectionCode(section));
        dispatch({ type: PRESS_BOOKMARKS_BUTTON });
        navigateToBookmarksPage();
      };
    case CTA.BOOKMARK:
      return dispatch => dispatch(addBookmark(options.item));
    case CTA.UNBOOKMARK:
      return dispatch => dispatch(removeBookmark(options.item));
    case CTA.CLOSE:
      return dispatch => {
        dispatch(setCurrentSectionCode(section));
        sendEvent(event);
        // dispatch(navigateToAliasHome());
        console.log('[CTA] Reloading Home on close');
        navigateToAliasHome({ reload });
      };
    case CTA.FOLDER:
      return dispatch => {
        dispatch(setCurrentSectionCode(section));
        sendEvent(event);
        // dispatch(navigateToFolderPage(id));
        navigateToFolderPage(id);
      };
    case CTA.FOLDERS:
      return dispatch => {
        dispatch(setCurrentSectionCode(section));
        sendEvent(event);
        navigateToFoldersPage();
      };
    case CTA.FRONTSPACE:
      return dispatch => {
        // NOTE: The "offers" layout may change a section from "kids" to "main", leading to an unintended content unlocking
        if (id !== LAYOUT_FRONTSPACES.offers) {
          dispatch(setCurrentSectionCode(section));
        }
        sendEvent(event);
        navigateToFrontspace(id);
      };
    case CTA.PROFILES_GATE:
      return dispatch => {
        sendEvent(event);
        dispatch(clearAfterProfileChanged());
        console.log('[CTA] Reloading Home to get Profiles Gate');
        navigateToAliasHome({ reload: true });
      };
    case CTA.HOME_ALIAS:
      return dispatch => {
        dispatch(setCurrentSectionCode(section));
        sendEvent(event);
        // dispatch(navigateToAliasHome());
        console.log('[CTA] Reloading Home');
        navigateToAliasHome({ reload });
      };
    case CTA.INFORMATION:
      return dispatch => {
        dispatch(setCurrentSectionCode(section));
        sendEvent(event);
        navigateToInformationPage();
      };
    case CTA.USAGE_TERMS:
      return () => {
        navigateToUsageTermsPage();
      };

    case CTA.SUBSCRIPTION_TERMS:
      return dispatch => {
        dispatch(setCurrentSectionCode(section));
        sendEvent(event);
        // TODO: navigate to Subscription Terms
        // dispatch(navigateToSubscriptionTermsPage());
      };
    case CTA.LEGAL_CONDITIONS:
      return dispatch => {
        dispatch(setCurrentSectionCode(section));
        sendEvent(event);
        navigateToLegalConditionsPage();
      };
    case CTA.ACCESSIBILITY_STATEMENT:
      return dispatch => {
        dispatch(setCurrentSectionCode(section));
        sendEvent(event);
        navigateToAccessibilityStatementPage();
      };
    case CTA.PRIVACY_POLICY:
      return () => {
        navigateToPrivacyPolicyPage();
      };
    case CTA.LIVE:
      return () => {
        sendEvent(event);
        // TODO: navigate to Live
        navigateToLivePage(id);
      };
    case CTA.LIVES:
      return dispatch => {
        dispatch(setCurrentSectionCode(section));
        sendEvent(event);
        navigateToLivesPage();
      };
    case CTA.LOCK:
      sendEvent(event);

      return handleLock({ item: options.item });
    case CTA.LOGOUT:
      return async (dispatch, getState) => {
        // eslint-disable-next-line no-console
        console.log('[CTA] Handling logout action');
        rememberSSIDeclined();
        await dispatch(logout());
        dispatch({ type: PRESS_LOGOUT_BUTTON });
        sendEvent({ key: 'pressLogoutButton' });
        dispatch(disableProfileAutoSelect());
        dispatch(resetJwtProfile());
        dispatch(clearCache());
        if (getOpenCatalogFeature(getState())) {
          dispatch(loadNavigation());
          navigateToAliasHome();
        } else {
          navigateToLandingPage();
        }
      };
    case CTA.OPTOUT:
      return () => {
        navigateToSettingsPage();
      };
    case CTA.PROFILE:
      return dispatch => {
        dispatch(setCurrentSectionCode(section));
        sendEvent(event);
        // dispatch(navigateToProfilePage());
        navigateToProfilePage();
      };
    case CTA.PROGRAM:
      return dispatch => {
        dispatch(setCurrentSectionCode(section));
        sendEvent(event);
        navigateToProgramPage(id);
      };
    case CTA.SEASON:
      return dispatch => {
        dispatch(setCurrentSectionCode(section));
        sendEvent(event);
        navigateToSeasonPage(id);
      };
    case CTA.SEARCH:
      return dispatch => {
        dispatch(setCurrentSectionCode(section));
        sendEvent(event);
        navigateToSearchPage();
      };
    case CTA.SERVICE:
      return dispatch => {
        dispatch(setCurrentSectionCode(section));
        sendEvent(event);
        // dispatch(navigateToServicePage(id));
        navigateToServicePage(id);
      };
    case CTA.SERVICES:
      return dispatch => {
        dispatch(setCurrentSectionCode(section));
        sendEvent(event);
        navigateToServicesPage();
      };
    case CTA.VIDEO:
      return () => {
        sendEvent(event);
        navigateToVodPage(id);
      };
    case CTA.HELP:
      return dispatch => {
        dispatch(setCurrentSectionCode(section));
        overrideGAPageName('main_App_help');
        sendEvent(event);
        navigateToFAQPage();
      };
    case CTA.FEEDBACK:
      return dispatch => {
        dispatch(setCurrentSectionCode(section));
        overrideGAPageName('main_App_feedback');
        sendEvent(event);
        navigateToFeedbackPage();
      };
    case CTA.ACCOUNT_SETTINGS:
      return dispatch => {
        dispatch(setCurrentSectionCode(section));
        sendEvent(event);
        navigateToAccountSettingsPage();
      };
    case CTA.PLAY:
      return (dispatch, getState) => {
        const state = Store.getState();
        const layout = getCurrentLayout(state);
        const entityType = getRequestedEntityType(state);

        dispatch(
          setPlayerInitialized({
            layout,
            isLive: entityType === LAYOUT_ENTITIES.live,
            entityType,
            ...options,
          }),
        );

        const source = getVideoSource(getState());
        if (!source) {
          dispatch(errorAction(ERROR_LEVELS.FATAL, ERROR_TYPES.PLAYER, 'No sources available'));
        }
      };
    default:
  }

  return null;
};

export const dispatchItemAction = item => {
  if (!item) {
    return;
  }

  const action = createItemAction({ item });
  if (action) {
    Store.dispatch(action);
  }
};
